import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Play, Check, Download, Loader2, Trash } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import MediaLightbox from "@/components/shared/MediaLightbox";
import { downloadSingle, downloadZip } from "@/lib/download";

export default function PhotosTab({ wedding }) {
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [previewIndex, setPreviewIndex] = useState(null);

  const load = () => api.get("/owner/photos").then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    try {
      await api.delete(`/owner/photos/${p.id}`);
      setPhotos((ps) => ps.filter((x) => x.id !== p.id));
      setSelected((s) => { const n = new Set(s); n.delete(p.id); return n; });
      setPreviewIndex(null);
      toast.success("Foto eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const allSelected = photos.length > 0 && selected.size === photos.length;
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(photos.map((p) => p.id)));

  const [deleting, setDeleting] = useState(false);

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} file selezionati?`)) return;
    setDeleting(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => api.delete(`/owner/photos/${id}`)));
      const failedCount = results.filter((r) => r.status === "rejected").length;
      const succeededIds = new Set(ids.filter((id, i) => results[i].status === "fulfilled"));
      setPhotos((ps) => ps.filter((p) => !succeededIds.has(p.id)));
      setSelected(new Set());
      if (failedCount) toast.error(`${failedCount} eliminazioni non riuscite`);
      else toast.success(`${ids.length} file eliminati`);
    } finally {
      setDeleting(false);
    }
  };

  // Both paths are plain links the server forces to save (Content-Disposition),
  // never fetch()/blob() in the browser -- the zip is now also assembled
  // server-side, so a flaky/blocked fetch can't produce an empty archive.
  const downloadSelected = () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (ids.length === 1) downloadSingle(photos.find((p) => p.id === ids[0]));
    else downloadZip({ ids, ownerWeddingId: wedding.id });
  };

  return (
    <div data-testid="photos-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h2 className="font-wed text-2xl">Moderazione galleria</h2>
        {photos.length > 0 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={toggleSelectAll} className="btn-outline-wed !py-2 !px-4 text-sm" data-testid="photos-select-all-button">
              {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
            </button>
            <button
              type="button"
              onClick={downloadSelected}
              disabled={selected.size === 0}
              className="btn-wed !py-2 !px-4 text-sm flex items-center gap-2"
              data-testid="photos-download-selected-button"
            >
              <Download size={15} />
              Scarica {selected.size > 0 ? `(${selected.size})` : ""}
            </button>
            <button
              type="button"
              onClick={deleteSelected}
              disabled={selected.size === 0 || deleting}
              className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2 !text-rose-500 !border-rose-200 hover:!bg-rose-50"
              data-testid="photos-delete-selected-button"
            >
              {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash size={15} />}
              Elimina {selected.size > 0 ? `(${selected.size})` : ""}
            </button>
          </div>
        )}
      </div>
      <p className="text-sm text-neutral-500 mb-6">{photos.length} file caricati dagli invitati — visibili solo a voi finché non pubblicate l'album (Pagina &amp; Tema). Tocca un file per vederlo in grande.</p>
      {photos.length === 0 ? (
        <p className="text-neutral-400 py-10 text-center border border-dashed rounded-2xl" style={{ borderColor: "#e3ddd2" }} data-testid="photos-empty">Nessuna foto caricata</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {photos.map((p, i) => (
            <div key={p.id} className="relative group rounded-xl overflow-hidden aspect-square" data-testid={`photo-mod-${p.id}`}>
              <button type="button" onClick={() => setPreviewIndex(i)} className="absolute inset-0 w-full h-full" data-testid={`photo-preview-open-${p.id}`}>
                {p.media_type === "video" ? (
                  <>
                    {p.thumbnail_url !== p.url ? (
                      <img src={assetUrl(p.thumbnail_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <video src={assetUrl(p.thumbnail_url)} className="w-full h-full object-cover" muted preload="metadata" />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                      <Play size={20} className="text-white drop-shadow" fill="white" />
                    </span>
                  </>
                ) : (
                  <img src={assetUrl(p.thumbnail_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                )}
              </button>
              {(p.caption || p.uploaded_by_name) && (
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
                  {p.caption && <p className="text-white text-xs truncate">{p.caption}</p>}
                  {p.uploaded_by_name && <p className="text-white/70 text-[0.65rem] truncate">— {p.uploaded_by_name}</p>}
                </div>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }}
                className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors duration-150"
                style={selected.has(p.id) ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)" } : { background: "rgba(255,255,255,0.8)", borderColor: "white" }}
                data-testid={`photo-select-${p.id}`}
              >
                {selected.has(p.id) && <Check size={14} className="text-white" />}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(p); }}
                className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity duration-200"
                data-testid={`photo-delete-${p.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {previewIndex != null && (
        <MediaLightbox items={photos} index={previewIndex} onChange={setPreviewIndex} onClose={() => setPreviewIndex(null)} onDelete={remove} />
      )}
    </div>
  );
}
